/**
 * The 3D view's mark on a selected plinth or ceiling molding: its strips painted over in the selection accent, a hair
 * in front of them. The outline the other selections wear is a shell grown round the object and seen from inside;
 * round a strip lying against its wall that shell stands in the wall and behind the floor, and nothing of it shows.
 * The copies share the strips' geometry, so a wall cut down or slid takes the mark with it; self-lit, no shadows, never
 * picked, and the root belongs to the scene, never the model.
 */
import * as THREE from 'three';

export class TrimSelection {
  readonly root = new THREE.Group();
  private material: THREE.MeshBasicMaterial | null = null;
  private stamp = '';
  private copies: THREE.Mesh[] = [];
  private disposed = false;

  constructor() {
    this.root.name = 'trim-selection';
    // The contact-shadow pass and the material preview skip anything carrying this flag.
    this.root.userData.selectionPresentation = true;
    this.root.matrixAutoUpdate = false;
    this.root.visible = false;
  }

  update(target: THREE.Object3D | null, enabled: boolean): void {
    if (this.disposed) return;
    if (!target) { this.clear(); return; }
    target.updateWorldMatrix(true, true);
    const meshes: THREE.Mesh[] = [];
    target.traverse(object => { if (object instanceof THREE.Mesh && !object.userData.selectionPresentation) meshes.push(object); });
    const stamp = meshes.map(mesh => `${mesh.uuid}:${mesh.geometry.uuid}:${mesh.matrixWorld.elements.join(',')}`).join('|');
    if (stamp !== this.stamp) {
      for (const copy of this.copies) copy.removeFromParent();
      this.material ??= new THREE.MeshBasicMaterial({ color: new THREE.Color().setRGB(.25, .52, 1, THREE.SRGBColorSpace), transparent: true, opacity: .55,
        depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4, toneMapped: false });
      this.copies = meshes.map(mesh => {
        const copy = new THREE.Mesh(mesh.geometry, this.material!);
        copy.name = 'trim-selection-copy'; copy.userData.selectionPresentation = true;
        copy.matrixAutoUpdate = false; copy.matrix.copy(mesh.matrixWorld); copy.castShadow = false; copy.receiveShadow = false;
        copy.raycast = () => {};
        this.root.add(copy);
        return copy;
      });
      this.stamp = stamp;
    }
    let shown = enabled && this.copies.length > 0;
    for (let node: THREE.Object3D | null = target; node && shown; node = node.parent) if (!node.visible) shown = false;
    this.root.visible = shown;
    this.root.matrix.identity();
    if (this.root.parent) {
      this.root.parent.updateWorldMatrix(true, false);
      this.root.matrix.copy(this.root.parent.matrixWorld).invert();
    }
    this.root.updateMatrixWorld(true);
  }

  /** How many strips are marked and whether the mark shows, for checks. */
  describe(): { visible: boolean; strips: number } { return { visible: this.root.visible, strips: this.copies.length }; }

  clear(): void {
    for (const copy of this.copies) copy.removeFromParent();
    this.copies = []; this.stamp = ''; this.root.visible = false;
  }

  dispose(): void {
    if (this.disposed) return;
    this.clear(); this.material?.dispose(); this.material = null; this.root.removeFromParent(); this.disposed = true;
  }
}
